import { contractorPrice } from "./contractor";
import { lineCost } from "./cost";
import { money, roundClient, share } from "./policy";
import { resolveRate } from "./ratecard";
import type { CalcPolicy, LineCalc, RatecardRole, ResourceLine, TaxScheme } from "./types";

/**
 * Расчёт одной ресурсной строки.
 *
 * Повторяет колонки шаблона: `H = G × F`, `I = H × ставка НДС`, `J = H + I`,
 * `Q = H − K`, `R = Q / H`.
 */
export function calcLine(
  line: ResourceLine,
  ctx: {
    rateIndex: Map<string, RatecardRole>;
    scheme: TaxScheme;
    policy: CalcPolicy;
  },
): LineCalc {
  const resolved = resolveRate(line, ctx.rateIndex);
  const { cost, vatIn, source } = lineCost(line, ctx.scheme);
  /*
   * Подрядная строка не берёт ставку из рейткарты: её цена считается наценкой.
   * Ставка за единицу выводится обратным ходом, чтобы остальная арифметика
   * (`H = G × F`) осталась прежней.
   *
   * Наценка идёт от себестоимости — того, во что строка обошлась вместе с
   * налогом на выплату, а не от одной договорной суммы. Иначе налог съедал бы
   * маржу молча: «наценка 54%» при НДС сверху давала бы рентабельность
   * заметно ниже той, что менеджер назначил. Пока налог нулевой, это то же
   * самое число, что и раньше.
   */
  /*
   * Условие — «расход заведён», а не «есть счёт». ОФК оказался наличным
   * платежом (ответ Braining, 31 августа), и подрядчик, которому заплатили
   * налом целиком, счёта не приносит вовсе. Пока условием был счёт, такая
   * строка теряла наценку и уходила по ставке из рейткарты — то есть по цене,
   * не имеющей отношения к тому, во что она обошлась.
   */
  const contracted =
    line.contractor && source === "exact"
      ? contractorPrice(cost, line.contractor, ctx.policy.clientRoundStep)
      : null;
  // Приоритет цены за единицу: счёт подрядчика → ручная правка с причиной →
  // замороженная/прямая цена → рейткарта.
  const rate =
    contracted !== null
      ? line.qty
        ? contracted / line.qty
        : contracted
      : (line.rateOverride?.rate ?? resolved.rate);
  const missing = contracted !== null || line.rateOverride ? false : resolved.missing;

  const net = line.exactPrice
    ? money(rate * line.qty)
    : roundClient(rate * line.qty, ctx.policy.clientRoundStep);
  const vat = money(net * ctx.scheme.vatRate);
  const gross = money(net + vat);
  const profit = money(net - cost);

  return {
    line,
    rate,
    rateMissing: missing,
    net,
    vat,
    gross,
    vatIn,
    cost,
    costSource: source,
    profit,
    margin: share(profit, net),
  };
}
